import { Icon } from "@circle-react-shared/Icon";
import { useProfileLinkProps } from "@circle-react/hooks/profile/useProfileLinkProps";
import type { CommunityMember } from "@circle-react/types";
import { useLayoutEffect, useRef, useState } from "react";

import { ConnectionButtonsOrchestrator } from "@/react/components/ConnectionButtons/ConnectionButtonsOrchestrator";
import { useIsMemberDirectoryMapAvailable } from "@/react/components/MemberDirectory/useIsMemberDirectoryMapAvailable";
import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { UserImage } from "@/react/components/SettingsApp/CompassCompat/UserImage";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";

import { MemberGradientBackground } from "./MemberGradientBackground";
import { useIsBioVisibleInProfileView } from "./useIsBioVisibleInProfileView";

export interface MemberCardWithConnectionProps {
  canReceiveDmFromCurrentMember?: boolean;
  member: CommunityMember;
}

export const MemberCardWithConnection = ({
  member,
  canReceiveDmFromCurrentMember,
}: MemberCardWithConnectionProps) => {
  const profileLinkProps = useProfileLinkProps({
    public_uid: member.public_uid ?? "",
  });

  const isConnectEnabled = useIsConnectEnabled();
  const isBioVisibleInProfileView = useIsBioVisibleInProfileView();
  const { isAvailable: isLocationVisible } = useIsMemberDirectoryMapAvailable();
  const headlineRef = useRef<HTMLSpanElement>(null);
  const [isHeadlineTruncated, setIsHeadlineTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = headlineRef.current?.querySelector("p");
    if (el) {
      setIsHeadlineTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [member.headline]);

  return (
    <li
      className="bg-primary outline-primary hover:shadow-c-card group relative inline-flex h-[262px] min-w-[218px] max-w-[244px] flex-col items-center justify-between overflow-hidden rounded-xl px-3 pb-4 pt-3 outline outline-1 outline-offset-[-1px]"
      data-testid="community_member_grid"
    >
      <div className="pointer-events-none absolute inset-3 z-0">
        <MemberGradientBackground member={member} className="inset-0" />
        {member.bio && isBioVisibleInProfileView && (
          <TippyV2 content={member.bio} shouldUseSpanWrapper={false}>
            <button
              type="button"
              className="text-default pointer-events-auto absolute right-0 top-0 z-10 p-2 leading-[0] opacity-0 transition-opacity group-hover:opacity-100"
            >
              <Icon type="16-info" size={16} className="!size-4" />
            </button>
          </TippyV2>
        )}
      </div>
      <a
        {...profileLinkProps}
        className={classNames(
          "flex w-full flex-col items-center justify-start gap-4 rounded-md py-4",
          {
            "h-full": !isConnectEnabled,
          },
        )}
        title={member.name}
      >
        <div className="size-16">
          <UserImage
            name={member.name ?? ""}
            src={member.avatar_url ?? ""}
            size="16"
          />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-2.5 px-2">
          <div className="flex w-full flex-col items-center justify-start gap-0.5">
            <span className="w-full text-center">
              <Typography.BodySm
                truncate
                color="text-darkest"
                as="h2"
                weight="font-semibold"
              >
                {member.name}
              </Typography.BodySm>
            </span>

            <span ref={headlineRef} className="w-full text-center">
              <TippyV2
                content={member.headline}
                disabled={!isHeadlineTruncated}
              >
                <p className="line-clamp-2 w-full text-center text-xs leading-5 tracking-tight text-default">
                  {member.headline}
                </p>
              </TippyV2>
            </span>
          </div>
          {member.location && isLocationVisible && (
            <TippyV2
              content={member.location}
              className="w-full"
              disabled={member.location.split(",")[0] === member.location}
            >
              <div className="flex w-full items-center justify-center gap-1 px-2">
                <Icon
                  type="16-location"
                  size={16}
                  className="text-default shrink-0"
                />
                <span className="min-w-0 text-center">
                  <Typography.BodyXs
                    truncate
                    color="text-default"
                    weight="font-medium"
                  >
                    {member.location.split(",")[0]}
                  </Typography.BodyXs>
                </span>
              </div>
            </TippyV2>
          )}
        </div>
      </a>
      <div className="inline-flex w-full items-start justify-start gap-2 px-1">
        <ConnectionButtonsOrchestrator
          member={member}
          className="w-full"
          fullWidth
          canReceiveDmFromCurrentMember={canReceiveDmFromCurrentMember}
        />
      </div>
    </li>
  );
};
